'use client';
import { FacebookShareButton, PinterestShareButton, TwitterShareButton } from 'react-share';
import BreadcrumbSeo from '@repo/ui/src/components/Seo/_BreadcrumbSEO';
import { useBreadcrumbs } from '@repo/ui/src/context/BreadCrumbProvider';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import PinterestIcon from '@repo/ui/src/components/Icons/PinterestIcon';
import FacebookIcon from '@repo/ui/src/components/Icons/FacebookIcon';
import TwitterIcon from '@repo/ui/src/components/Icons/TwitterIcon';
import Container from '@repo/ui/src/components/Container';
import { classNames } from '@repo/ui/src/utils/classNames';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { LinkProps } from '@repo/ui/src/types/link';
import { SeoProps } from '@repo/ui/src/types/seo';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

interface Props {
  breadcrumb?: Array<LinkProps>;
  showSocial?: boolean;
  seoObj?: SeoProps;
  className?: string;
  title?: string;
  dark?: boolean;
}

const iconProps = {
  backFillColor: '#25282a',
  frontFillColor: '#fff',
};

const Breadcrumb = ({ breadcrumb, showSocial, seoObj, className, title, dark }: Props) => {
  const { path } = useCurrentUrl();
  const { darkBackground } = useBreadcrumbs();

  if (!breadcrumb?.length) {
    return null;
  }

  const imgUrl = buildImageSrc(seoObj?.ogImage, 600, 1, 1);
  const pageUrl = `${process.env.NEXT_PUBLIC_WEB_ENV}/${path}`;

  return (
    <Container
      className={classNames(
        'hidden py-8 absolute z-[102]',
        showSocial ? 'md:flex justify-between w-full' : 'md:inline-block',
        className,
      )}
    >
      <BreadcrumbSeo breadcrumbs={breadcrumb.map(buildLinkObj)} />
      <nav aria-label="breadcrumb">
        <ol
          className={classNames(
            'list-none grid grid-flow-col items-baseline pl-0 gap-x-1.5',
            (dark || darkBackground) && 'text-white',
          )}
        >
          {breadcrumb?.map((link, i) => (
            <li
              key={i}
              className={classNames(
                "tra-text-web-body-m text-inherit text-xs grid grid-flow-col items-baseline gap-x-1.5 after:content-['/'] after:inline-block",
                i === breadcrumb.length - 1 && !title && 'after:content-none',
              )}
            >
              <Anchor
                {...link}
                className="tra-text-web-body-m text-xs text-inherit hover:underline"
              >
                {link?.displayName}
              </Anchor>
            </li>
          ))}
          {title && (
            <li
              key="pageTitle"
              className={classNames('tra-text-web-body-m text-xs text-inherit')}
              itemProp="name"
            >
              {title}
            </li>
          )}
        </ol>
      </nav>
      {showSocial && (
        <ul className="list-none m-0 p-0 grid grid-flow-col gap-x-4">
          {seoObj?.ogImage && (
            <li>
              <PinterestShareButton media={imgUrl} url={pageUrl}>
                <PinterestIcon {...iconProps} />
              </PinterestShareButton>
            </li>
          )}
          <li>
            <TwitterShareButton url={pageUrl}>
              <TwitterIcon {...iconProps} />
            </TwitterShareButton>
          </li>
          <li>
            <FacebookShareButton url={pageUrl}>
              <FacebookIcon {...iconProps} />
            </FacebookShareButton>
          </li>
        </ul>
      )}
    </Container>
  );
};

export default Breadcrumb;
